/* eslint-disable @typescript-eslint/no-explicit-any */
import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { CookieConsentStore } from '@elio/emanager-front/cookie-consent/feature';
import { MessagingSubscriptionActions } from '@elio/emanager-front/push-messaging/action';
import { AppConfigService } from '@elio/emanager-front/shared/app-config';
import { LocalizationStore } from '@elio/emanager-front/shared/localization';
import { FILTERABLE_STORE, FilterStore } from '@elio/emanager-front/shared/ui/filters';
import { CheckForUpdateStore } from '@elio/shared/check-for-update';
import { provideComponentStore } from '@ngrx/component-store';
import { Actions, ofType } from '@ngrx/effects';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'elio-root',
  templateUrl: './app.component.html',
  providers: [provideComponentStore(CheckForUpdateStore)],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  displayCookieConsent$ = this.cookieConsentStore.getDisplayCookieConsent$;

  private destroyed$ = new Subject<void>();

  constructor(
    localizationStore: LocalizationStore,
    checkUpdateStore: CheckForUpdateStore,
    private cookieConsentStore: CookieConsentStore,
    private appConfigService: AppConfigService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(FILTERABLE_STORE) private filterStore: FilterStore<never>,
    private actions$: Actions
  ) {
    localizationStore.init();
    localizationStore.locale$
      .pipe(takeUntil(localizationStore.destroy$))
      .subscribe((locale) => (this.document.documentElement.lang = locale));
    checkUpdateStore.init();
    cookieConsentStore.init();
  }

  ngOnInit(): void {
    this.googleAnalyticsSetup();

    this.actions$
      .pipe(
        ofType(MessagingSubscriptionActions.successAndLogout, MessagingSubscriptionActions.failureAndLogout),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.filterStore.cleanupOnLogout();
      });
  }

  private googleAnalyticsSetup() {
    const envGaId = this.appConfigService.getAppConfig().googleAnalyticsId;
    (window as any).googleAnalyticsTrackingId = envGaId;
    const script = this.renderer.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${envGaId}`;

    const existingScript = document.querySelector('script[src*="googletagmanager"]');
    if (existingScript) {
      existingScript.remove();
    }
    this.renderer.appendChild(document.head, script);

    script.onload = () => {
      const dataLayer: unknown[] = (window as any).dataLayer || [];
      dataLayer.push('js', new Date());
      dataLayer.push('config', envGaId);
    };

    script.onerror = (error?: Error) => {
      console.error('An error has occured in the google analytics script :', error);
    };
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
